import React, { FC } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDropzone } from 'react-dropzone';
// mui components
import { Grid, IconButton, Typography } from '@mui/material';
// custom hooks
import useToast from 'hooks/useToast';
// api
import {
  useLazyGetFileCabinetSelectionUploadURlQuery,
  usePostFileToAWSMutation,
} from 'redux-store/api/file-cabinet';
// types
import { ISelectionUploadingFile } from 'redux-store/api/selections';
import { ErrorCode } from 'react-dropzone';
// constants
import { FILE_TYPES, IMAGE_TYPES, MAX_FILE_SIZE } from 'utils/constants';
// icons
import UploadButtonIcon from 'assets/icons/new/selection/upload-button-icon.svg';
// styles
import { styles } from './styles';
import axios from 'axios';

interface IProps {
  projectId?: string | number;
  appendFiles: (files: ISelectionUploadingFile[]) => void;
  updateFile: (tempId: string, updatingData: Partial<ISelectionUploadingFile>) => void;
}

const UploadedFile: FC<IProps> = ({ projectId, appendFiles, updateFile }) => {
  const [getUploadUrl] = useLazyGetFileCabinetSelectionUploadURlQuery();
  const [postFileToAWS] = usePostFileToAWSMutation();

  const showToast = useToast();
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    multiple: true,
    maxSize: MAX_FILE_SIZE,
    accept: `${IMAGE_TYPES}, ${FILE_TYPES}`,
    noClick: true,
    noKeyboard: true,
    onDrop: async (acceptedFiles, rejectedFiles) => {
      // handle rejected files

      if (rejectedFiles?.length) {
        if (
          rejectedFiles.find(({ errors }) =>
            errors.find(({ code }) => code === ErrorCode.FileInvalidType)
          )
        ) {
          showToast({ type: 'error', message: 'This file type is not supported.' });
        }
        const filteredRejectedFiles = rejectedFiles.filter(({ errors }) =>
          errors.find(({ code }) => code === ErrorCode.FileTooLarge)
        );
        const rejectedUploads: ISelectionUploadingFile[] = filteredRejectedFiles.map(({ file }) => {
          return {
            size: file.size,
            name: file.name,
            status: 'error_file_size',
            percent: 0,
            id: null,
            url: '',
            tempId: uuidv4(),
            mimeType: file.type,
          };
        });

        appendFiles(rejectedUploads);
      }

      if (!acceptedFiles?.length) {
        return;
      }

      // map accepted
      const files = acceptedFiles.map((file) => ({
        tempId: uuidv4(),
        file,
      }));

      // add accepted to state
      appendFiles(
        files.map(({ file: { name, size, type }, tempId }) => ({
          id: null,
          url: '',
          status: 'preparing',
          size,
          name,
          tempId,
          percent: 0,
          mimeType: type,
        }))
      );

      // get upload url and post to AWS each file
      try {
        await Promise.all(
          files.map(async ({ file, tempId }) => {
            await getUploadUrl({
              key: file.name,
              mimeType: file.type,
              ...(!!projectId && { projectId }),
            })
              .unwrap()
              .then(async (res) => {
                const formData = new FormData();
                formData.append('file', file);
                await handlePostFileToAWS(res, file, tempId);
              })
              .catch(() => {
                showToast({ type: 'error', message: 'Something went wrong!' });
              });
          })
        );
      } catch (err) {
        showToast({ type: 'error', message: 'Something went wrong!' });
      }
    },
  });

  const handlePostFileToAWS = async (res: any, file: File, tempId: string) => {
    try {
      await axios({
        method: 'PUT',
        baseURL: process.env.REACT_APP_API_URL,
        url: res.url,
        data: file,
        headers: {
          'Content-Type': file.type,
        },
        onUploadProgress: (event) => {
          const percent = Math.round((event.loaded * 100) / (event.total ?? 1));
          updateFile(tempId, { percent, status: 'uploading' });
        },
      });

      updateFile(tempId, { percent: 100, status: 'done', id: res.id });
    } catch (error) {
      showToast({ type: 'error', message: 'Something went wrong!' });
    }
  };

  return (
    <Grid {...getRootProps()} sx={{ display: 'flex', width: '100%' }}>
      <Grid
        sx={{
          ...styles.textEditorStyles.container,
          height: '160px',
          border: isDragActive ? '2px dashed #4D6EF5' : '2px dashed #D4D6E0',
        }}
      >
        <input type="file" {...getInputProps()} placeholder="FILE" />
        <Typography sx={styles.textEditorStyles.title}>Drag & drop your file here</Typography>
        <Typography sx={styles.textEditorStyles.title}>or</Typography>
        <IconButton onClick={open}>
          <Grid display="flex" sx={styles.customButtonStyle}>
            <UploadButtonIcon />
            <Typography sx={{ fontFamily: 'Inter', fontSize: '14px' }}>Upload</Typography>
          </Grid>
        </IconButton>

        <Typography sx={styles.textEditorStyles.subtitle}>Maximum file size 50 MB</Typography>
      </Grid>
    </Grid>
  );
};

export default UploadedFile;
